import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FaStop } from 'react-icons/fa';
import './SmsSendHistory.scss';

const HISTORY_TABS = [
    { key: 'sms-wait', label: '발송대기(단문)' },
    { key: 'sms-complete', label: '발송완료(단문)' },
    { key: 'mms-wait', label: '발송대기(장문)' },
    { key: 'mms-complete', label: '발송완료(장문)' },
    { key: 'usage', label: '사용내역' }
];

const MONTHS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, '0'));

function apiUrl(path) {
    const base = String(process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '');
    return `${base}${path}`;
}

function statusLabel(value) {
    const code = String(value ?? '');
    if (code === '0') return '발송대기';
    if (code === '2') return '전송완료';
    if (code === '3') return '결과수신완료';
    return code;
}

function resultLabel(value) {
    const code = String(value ?? '');
    const labels = {
        0: '성공',
        32: '메시지초과',
        100: '발신번호오류',
        101: '발신번호오류',
        110: '수신시간 만료',
        201: '통신사기타오류',
        504: '파일 포맷오류'
    };
    return labels[code] || (code ? '오류' : '');
}

function formatNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number.toLocaleString('ko-KR') : '0';
}

function formatMoney(value) {
    return `${formatNumber(Math.round(Number(value) || 0))}원`;
}

const SmsSendHistory = () => {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
    const [view, setView] = useState('sms-complete');
    const [rows, setRows] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());
    const [checkedKeys, setCheckedKeys] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const rowDragSelectionRef = useRef({ active: false, mode: 'add' });
    const isWaitView = view === 'sms-wait' || view === 'mms-wait';
    const isMmsView = view === 'mms-wait' || view === 'mms-complete';
    const showMonthSelector = view !== 'sms-wait' && view !== 'mms-wait';
    const messageColumnCount = 8
        + (isMmsView && isWaitView ? 1 : 0)
        + (!isWaitView ? 4 : 0)
        + (isWaitView ? 1 : 0);

    const loadHistory = useCallback(async () => {
        setLoading(true);
        setError('');
        setSelectedMessage('');
        setSelectedRowKeys(new Set());
        setCheckedKeys(new Set());
        try {
            const response = await axios.post(apiUrl('/smsRoutes/history'), {
                view,
                month: `${year}${month}`
            });
            setRows(Array.isArray(response.data?.rows) ? response.data.rows : []);
        } catch (requestError) {
            setRows([]);
            setError(requestError.response?.data?.message || '문자내역을 조회하지 못했습니다.');
        } finally {
            setLoading(false);
        }
    }, [view, year, month]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    useEffect(() => {
        const stopRowDragSelection = () => {
            rowDragSelectionRef.current.active = false;
        };

        window.addEventListener('mouseup', stopRowDragSelection);
        return () => window.removeEventListener('mouseup', stopRowDragSelection);
    }, []);

    const checkedAll = rows.length > 0 && rows.every((row) => checkedKeys.has(String(row.sno)));
    const totalAmount = useMemo(
        () => rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
        [rows]
    );

    const toggleAll = () => {
        if (checkedAll) {
            setCheckedKeys(new Set());
            return;
        }
        setCheckedKeys(new Set(rows.map((row) => String(row.sno))));
    };

    const toggleChecked = (sno) => {
        const key = String(sno);
        setCheckedKeys((previous) => {
            const next = new Set(previous);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleRowMouseDown = (event, rowKey, message, index) => {
        if (event.button !== 0) return;

        event.preventDefault();
        const preserveSelection = event.ctrlKey || event.metaKey;
        const mode = preserveSelection && selectedRowKeys.has(rowKey) ? 'remove' : 'add';
        const baseKeys = preserveSelection ? new Set(selectedRowKeys) : new Set();
        rowDragSelectionRef.current = { active: true, mode, startIndex: index, baseKeys };
        setSelectedMessage(message || '');

        const nextKeys = new Set(baseKeys);
        if (mode === 'remove') nextKeys.delete(rowKey);
        else nextKeys.add(rowKey);
        setSelectedRowKeys(nextKeys);
    };

    const handleRowMouseEnter = (event, index) => {
        if (!rowDragSelectionRef.current.active) return;
        if ((event.buttons & 1) !== 1) {
            rowDragSelectionRef.current.active = false;
            return;
        }

        const { mode, startIndex, baseKeys } = rowDragSelectionRef.current;
        const nextKeys = new Set(baseKeys);
        const firstIndex = Math.min(startIndex, index);
        const lastIndex = Math.max(startIndex, index);

        for (let rowIndex = firstIndex; rowIndex <= lastIndex; rowIndex += 1) {
            const rangeRow = rows[rowIndex];
            if (!rangeRow) continue;
            const rangeRowKey = String(rangeRow.sno || rowIndex);
            if (mode === 'remove') nextKeys.delete(rangeRowKey);
            else nextKeys.add(rangeRowKey);
        }

        setSelectedRowKeys(nextKeys);
    };

    const cancelReservations = async () => {
        if (checkedKeys.size === 0) {
            window.alert('대상을 체크하세요!');
            return;
        }
        if (!window.confirm(`${checkedKeys.size}명의 예약을 삭제하시겠습니까?`)) return;

        try {
            const response = await axios.post(apiUrl('/smsRoutes/history/cancel'), {
                view,
                keys: [...checkedKeys]
            });
            window.alert(`${response.data?.deletedCount || 0}건 취소되었습니다.`);
            loadHistory();
        } catch (requestError) {
            window.alert(requestError.response?.data?.message || '예약취소에 실패했습니다.');
        }
    };

    const renderUsageTable = () => (
        <table className="sms-history-table usage-table">
            <thead>
                <tr>
                    <th>구분</th>
                    <th>월</th>
                    <th>전체개수</th>
                    <th>실패개수</th>
                    <th>성공개수</th>
                    <th>단가</th>
                    <th>금액</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => (
                    <tr key={row.kind}>
                        <td>{row.kind}</td>
                        <td>{row.month}</td>
                        <td>{formatNumber(row.total_count)}</td>
                        <td>{formatNumber(row.fail_count)}</td>
                        <td>{formatNumber(row.success_count)}</td>
                        <td>{formatMoney(row.unit_price)}</td>
                        <td>{formatMoney(row.amount)}</td>
                    </tr>
                ))}
                {rows.length > 0 && (
                    <tr className="usage-total-row">
                        <td colSpan="6">합계</td>
                        <td>{formatMoney(totalAmount)}</td>
                    </tr>
                )}
                {!loading && rows.length === 0 && (
                    <tr><td className="history-empty" colSpan="7">내역이 없습니다.</td></tr>
                )}
            </tbody>
        </table>
    );

    const renderMessageTable = () => (
        <table className="sms-history-table message-table">
            <thead>
                <tr>
                    <th>{selectedRowKeys.size > 0 ? selectedRowKeys.size : rows.length}</th>
                    <th>작성일</th>
                    <th>도착일</th>
                    <th>모드</th>
                    <th>성명</th>
                    <th>전화번호</th>
                    <th>내용</th>
                    <th>발송인</th>
                    {isMmsView && isWaitView && <th>FILE_CNT</th>}
                    {!isWaitView && <th>코드</th>}
                    {!isWaitView && <th>결과</th>}
                    {!isWaitView && <th>전송상태</th>}
                    {!isWaitView && <th>통신사</th>}
                    {isWaitView && (
                        <th className="check-column">
                            <input type="checkbox" checked={checkedAll} onChange={toggleAll} aria-label="전체 선택" />
                        </th>
                    )}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, index) => {
                    const rowKey = String(row.sno || index);
                    return (
                    <tr
                        key={rowKey}
                        className={selectedRowKeys.has(rowKey) ? 'is-selected' : ''}
                        onMouseDown={(event) => handleRowMouseDown(event, rowKey, row.message, index)}
                        onMouseEnter={(event) => handleRowMouseEnter(event, index)}
                        onDragStart={(event) => event.preventDefault()}
                    >
                        <td>{index + 1}</td>
                        <td>{row.created_at}</td>
                        <td>{row.send_at}</td>
                        <td>{row.send_mode}</td>
                        <td>{row.user_name}</td>
                        <td>{row.phone}</td>
                        <td className="message-cell" title={row.message}>{row.message}</td>
                        <td>{row.sender}</td>
                        {isMmsView && isWaitView && <td>{row.file_count}</td>}
                        {!isWaitView && <td>{row.result_code}</td>}
                        {!isWaitView && <td>{resultLabel(row.result_code)}</td>}
                        {!isWaitView && <td>{statusLabel(row.status_code)}</td>}
                        {!isWaitView && <td>{row.carrier}</td>}
                        {isWaitView && (
                            <td
                                className="check-column"
                                onMouseDown={(event) => event.stopPropagation()}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <input
                                    type="checkbox"
                                    checked={checkedKeys.has(String(row.sno))}
                                    onChange={() => toggleChecked(row.sno)}
                                    aria-label={`${index + 1}번 선택`}
                                />
                            </td>
                        )}
                    </tr>
                    );
                })}
                {rows.length > 0 && (
                    <tr className="history-end-spacer" aria-hidden="true">
                        <td colSpan={messageColumnCount} />
                    </tr>
                )}
                {!loading && rows.length === 0 && (
                    <tr><td className="history-empty" colSpan={messageColumnCount}>내역이 없습니다.</td></tr>
                )}
            </tbody>
        </table>
    );

    return (
        <div className="SmsSendHistory">
            <div className={`history-month-bar ${showMonthSelector ? '' : 'is-disabled'}`}>
                <button type="button" className="year-arrow" onClick={() => setYear((value) => value - 1)} disabled={!showMonthSelector}>◀</button>
                <button type="button" className="history-year" onClick={() => setYear(now.getFullYear())} disabled={!showMonthSelector}>{year}년</button>
                <button
                    type="button"
                    className="year-current"
                    onClick={() => setYear(now.getFullYear())}
                    disabled={!showMonthSelector}
                    title="현재 연도"
                    aria-label="현재 연도"
                >
                    <FaStop />
                </button>
                <button type="button" className="year-arrow" onClick={() => setYear((value) => value + 1)} disabled={!showMonthSelector}>▶</button>
                <div className="history-months">
                    {MONTHS.map((monthValue) => (
                        <label key={monthValue}>
                            <input
                                type="radio"
                                name="sms-history-month"
                                value={monthValue}
                                checked={month === monthValue}
                                onChange={() => setMonth(monthValue)}
                                disabled={!showMonthSelector}
                            />
                            {Number(monthValue)}월
                        </label>
                    ))}
                    <button
                        type="button"
                        className="history-today"
                        onClick={() => {
                            setYear(now.getFullYear());
                            setMonth(String(now.getMonth() + 1).padStart(2, '0'));
                        }}
                        disabled={!showMonthSelector}
                    >
                        오늘
                    </button>
                </div>
            </div>

            <div className="history-sub-tabs">
                {HISTORY_TABS.map((item) => (
                    <button
                        type="button"
                        key={item.key}
                        className={[
                            view === item.key ? 'active' : '',
                            item.key.startsWith('mms-') ? 'is-mms' : '',
                            item.key.endsWith('-complete') ? 'is-complete' : ''
                        ].filter(Boolean).join(' ')}
                        onClick={() => setView(item.key)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            {error && <div className="history-error">{error}</div>}
            <div className="history-grid-wrap">
                {view === 'usage' ? renderUsageTable() : renderMessageTable()}
                {loading && <div className="history-loading">조회 중...</div>}
            </div>

            {view !== 'usage' && (
                <div className={`history-message-preview ${isMmsView ? 'is-mms' : ''}`}>
                    {selectedMessage}
                </div>
            )}

            {isWaitView && (
                <div className="history-actions">
                    <button type="button" className="history-refresh" onClick={loadHistory} disabled={loading}>새로고침</button>
                    <button type="button" className="history-cancel" onClick={cancelReservations}>SMS 예약취소</button>
                </div>
            )}
        </div>
    );
};

export default SmsSendHistory;
